import {useState, lazy, Suspense} from 'react'
import {AgentIcon, type Icon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, IconButton, Spinner} from '@primer/react'
import type {Repository} from '@github-ui/current-repository'
import type {CreateTaskReference} from '@github-ui/agent-sessions/components/new-task-chat-input/NewTaskChatInput'
import {useClientValue} from '@github-ui/use-client-value'

export type NewAgentTaskButtonProps = {
  isIconButton?: boolean
  icon?: Icon
  open?: boolean
  label?: string
  onOpen?: () => void
  onClose?: () => void
  setContentOpen?: (newOpen: boolean) => void
  currentRepo?: Repository
  currentReference?: CreateTaskReference
  hideRepoSelector?: boolean
  hideIcon?: boolean
  eventType: string
  size?: 'small' | 'medium' | 'large'
  tooltipDirection?: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
}

export {NewAgentTaskButton}
const LazyNewAgentTaskButtonContent = lazy(() =>
  // eslint-disable-next-line github/no-then
  import('./NewAgentTaskButtonContent').then(m => ({default: m.NewAgentTaskButtonContent})),
)

function NewAgentTaskButton({
  isIconButton,
  icon = AgentIcon,
  label = 'New agent task',
  onOpen,
  onClose,
  setContentOpen,
  open,
  currentRepo,
  currentReference,
  hideRepoSelector,
  eventType,
  size,
  tooltipDirection,
}: NewAgentTaskButtonProps) {
  const [isOpen, setOpen] = useState(!!open)
  const [isSSR] = useClientValue(() => false, true, [])
  const handleOpen = onOpen ?? (() => setOpen(true))
  const handleClose = onClose ?? (() => setOpen(false))
  const handleContentOpen = setContentOpen ?? setOpen

  return (
    <AnchoredOverlay
      width="large"
      align="end"
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      renderAnchor={props => {
        return isIconButton ? (
          <IconButton
            icon={icon}
            aria-label={label}
            // We shouldn't need this but the typing requires something
            aria-labelledby={undefined}
            size={size}
            tooltipDirection={tooltipDirection}
            {...props}
          />
        ) : (
          <Button {...props} leadingVisual={icon} size={size}>
            {label}
          </Button>
        )
      }}
    >
      {!isSSR && (
        <Suspense fallback={<FallbackContent />}>
          <LazyNewAgentTaskButtonContent
            currentRepo={currentRepo}
            currentReference={currentReference}
            hideRepoSelector={hideRepoSelector}
            setOpen={handleContentOpen}
            eventType={eventType}
          />
        </Suspense>
      )}
    </AnchoredOverlay>
  )
}

function FallbackContent() {
  return (
    <div className="d-flex flex-column flex-items-center p-6">
      <Spinner srText="Loading..." />
    </div>
  )
}

try{ LazyNewAgentTaskButtonContent.displayName ||= 'LazyNewAgentTaskButtonContent' } catch {}
try{ NewAgentTaskButton.displayName ||= 'NewAgentTaskButton' } catch {}
try{ FallbackContent.displayName ||= 'FallbackContent' } catch {}